import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Connect to All VMs',
    description: 'Establish SSH connections to all three database server instances and verify their current PostgreSQL state.',
    requirements: [
      {
        text: 'Connect to the primary database server via SSH'
      },
      {
        text: 'Connect to replica1 database server via SSH'
      },
      {
        text: 'Connect to replica2 database server via SSH'
      },
      {
        text: 'Check PostgreSQL service status on each server'
      },
      {
        text: 'Verify which server is intended to be the primary'
      }
    ],
    verification: [
      'You can connect to all three database servers',
      'PostgreSQL is running on all three servers',
      'You have identified the primary database server',
      'You understand the current broken replication state',
      'All servers are accessible via SSH'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Configure Primary Server for Replication',
    description: 'Fix the broken PostgreSQL primary server configuration to enable streaming replication with both replica servers.',
    requirements: [
      {
        text: 'Fix postgresql.conf on the primary server:',
        subitems: [
          'Change listen_addresses to allow replica server connections',
          'Set wal_level to "replica" instead of "minimal"',
          'Configure max_wal_senders to at least 2 for replica connections',
          'Set wal_keep_size to retain WAL files for replicas',
          'Add max_replication_slots setting (set to 2)'
        ]
      },
      {
        text: 'Create a proper replication user with correct privileges'
      },
      {
        text: 'Update pg_hba.conf to allow replication connections from replica servers'
      },
      {
        text: 'Restart PostgreSQL on primary server to apply configuration changes'
      },
      {
        text: 'Create replication slots for both replica servers'
      }
    ],
    verification: [
      'WAL level shows "replica" on primary server',
      'Replication user "replicator" has REPLICATION and LOGIN privileges',
      'pg_hba.conf allows replication connections from replica servers',
      'Primary server accepts connections from replica servers',
      'Primary server is ready for replication setup'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Configure Replica1 Server',
    description: 'Set up the first replica server to connect to the primary and start streaming replication.',
    requirements: [
      {
        text: 'Stop PostgreSQL service on replica1 server'
      },
      {
        text: 'Clear the PostgreSQL data directory on replica1'
      },
      {
        text: 'Use pg_basebackup to copy initial data from primary to replica1'
      },
      {
        text: 'Configure postgresql.conf on replica1 for standby mode:',
        subitems: [
          'Enable hot_standby for read queries',
          'Keep max_wal_senders compatible with the primary value',
          'Set primary_conninfo to connect to primary server',
          'Configure primary_slot_name to use replica1_slot'
        ]
      },
      {
        text: 'Create standby.signal file in the data directory'
      },
      {
        text: 'Start PostgreSQL service on replica1'
      }
    ],
    verification: [
      'Replica1 is in recovery mode (pg_is_in_recovery returns true)',
      'replica1_slot is active on primary server',
      'Replica1 is streaming from primary server',
      'standby.signal file exists on replica1',
      'Replica1 can handle read queries'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Configure Replica2 Server',
    description: 'Set up the second replica server for high availability and load distribution.',
    requirements: [
      {
        text: 'Stop PostgreSQL service on replica2 server'
      },
      {
        text: 'Clear the PostgreSQL data directory on replica2'
      },
      {
        text: 'Use pg_basebackup to copy initial data from primary to replica2'
      },
      {
        text: 'Configure postgresql.conf on replica2 for standby mode:',
        subitems: [
          'Enable hot_standby for read queries',
          'Keep max_wal_senders compatible with the primary value',
          'Set primary_conninfo to connect to primary server',
          'Configure primary_slot_name to use replica2_slot'
        ]
      },
      {
        text: 'Create standby.signal file in the data directory'
      },
      {
        text: 'Start PostgreSQL service on replica2'
      }
    ],
    verification: [
      'Replica2 is in recovery mode (pg_is_in_recovery returns true)',
      'replica2_slot is active on primary server',
      'Replica2 is streaming from primary server',
      'standby.signal file exists on replica2',
      'Replica2 can handle read queries'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Document Failover Readiness',
    description: 'Prepare failover notes while leaving the original primary streaming to both replicas for grading.',
    requirements: [
      {
        text: 'Document the current replication status on all servers'
      },
      {
        text: 'Do not stop PostgreSQL on the original primary before grading'
      },
      {
        text: 'Document the commands you would use to promote replica1'
      },
      {
        text: 'Document how replica2 would be reconfigured to follow replica1 after promotion'
      },
      {
        text: 'Document how applications would be pointed at the promoted server'
      },
      {
        text: 'Keep the final graded state as primary -> replica1 and primary -> replica2 streaming replication'
      }
    ],
    verification: [
      'Original primary is still running',
      'Replica1 remains in recovery mode',
      'Replica2 remains in recovery mode',
      'Both replication slots are active on the primary',
      'Failover procedure is documented for later practice'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Test Replication and Data Sync',
    description: 'Verify that replication is working correctly by testing data synchronization between primary and replica servers.',
    requirements: [
      {
        text: 'Verify that the testdb database exists on all servers'
      },
      {
        text: 'Check existing test data in the test_data table on all servers'
      },
      {
        text: 'Insert new test data on the primary server'
      },
      {
        text: 'Verify the new data appears on both replica servers within seconds'
      },
      {
        text: 'Test read queries on both replica servers'
      },
      {
        text: 'Confirm that replicas reject write operations'
      }
    ],
    verification: [
      'testdb database exists on primary server',
      'testdb database is replicated to replica1 server', 
      'testdb database is replicated to replica2 server',
      'New data inserted on primary appears on replicas quickly',
      'Read queries work correctly on both replica servers',
      'Write queries are properly rejected on replica servers'
    ]
  },
  {
    id: 'task-7',
    number: 7,
    title: 'Monitor and Verify Replication Status',
    description: 'Use monitoring tools to verify the health and performance of the PostgreSQL replication cluster.',
    requirements: [
      {
        text: 'Run the provided monitoring script to check overall status'
      },
      {
        text: 'Verify both replicas are in recovery mode'
      },
      {
        text: 'Check replication connections and slots on the primary'
      },
      {
        text: 'Monitor replication lag between primary and replicas'
      },
      {
        text: 'Verify that replication slots are actively used'
      },
      {
        text: 'Check PostgreSQL logs for any replication errors or warnings'
      }
    ],
    verification: [
      'Monitoring script runs successfully and shows healthy status',
      'Both replicas report being in recovery mode',
      'Primary shows active streaming replication connections',
      'Replication lag is minimal (under 1 second)',
      'Both replication slots show active usage',
      'No replication errors appear in PostgreSQL logs'
    ]
  }
];

export function DatabaseReplicationInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-5 py-2 bg-gradient-to-r from-[#10b981] to-[#059669] text-[#1a1f2e] rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default DatabaseReplicationInstructions;
